<script>
import Swal from "sweetalert2";
import CopasstDocAPI from "@/services/standards/copasst/copasstDocAPI";
import uploadDocument from "@/utils/digitalOcean/digitalocean-upload-file";
import newScoresAPI from "../../../services/newScores/newScoresAPI";
import { dateFormat } from "@/utils/helpers/format";

const BUCKET_BASE =
  "https://idmrnd3cibfs.compat.objectstorage.us-ashburn-1.oraclecloud.com/e-mision-sst/standards/";

// Formato descargable (bucket) por código de documento.
const FORMATOS = {
  "1.1.6.1": "ACTA_ASIGNACION_VIGIA_COPASST.docx",
  "1.1.6.2": "REGISTRO_ACTAS_MENSUALES_COPASST.docx",
  "1.1.6.3": "FORMATO_INSCRIPCION_COPASST.docx",
  "1.1.6.4": "ACTA_APERTURA_CIERRE_ELECCIONES_COPASST.docx",
  "1.1.6.5": "REGISTRO_VOTOS_COPASST.docx",
  "1.1.6.6": "ACTA_CONFORMACION_COPASST.docx",
  "1.1.6.7": "INFORME_TRIMESTRAL_COPASST.docx",
  "1.1.6.8": "INFORME_ANUAL_COPASST.docx",
};

/**
 * Item 1.1.6 - Documentos / formatos del COPASST.
 * Dropdown para descargar el formato y subir el respectivo soporte (similar al 1.1.8).
 */
export default {
  name: "CopasstDocumentos",
  props: {
    companyID: { type: [Number, String], default: null },
  },
  data() {
    return {
      // Opción por defecto: ver el listado de soportes subidos.
      selected: { id: 0, code: null, name: "Listado de soportes subidos" },
      list: [],
      historial: [],
      file: null,
      submitted: false,
      loading: true,
      nit: this.$store.getters["company/getCompany"].nit || null,
    };
  },
  methods: {
    dateConvert(date) {
      return dateFormat(date);
    },

    /** Permiso del usuario. */
    getRole(name) {
      const role = this.$store.state.auth.info.role || null;
      if (!role) return false;
      const permissions = this.$store.state.auth.info.permissions || [];
      return permissions.some((p) => p.name === name);
    },

    getTypes() {
      CopasstDocAPI.getTypes()
        .then((response) => {
          const list = response || [];
          list.unshift({ id: 0, code: null, name: "Listado de soportes subidos" });
          this.list = list;
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    getHistorial() {
      CopasstDocAPI.listForCompany(this.companyID)
        .then((response) => {
          this.historial = response && response.data ? response.data : [];
        })
        .catch((error) => {
          console.log(error);
        });
    },

    /** Descargar el formato del documento seleccionado. */
    print() {
      const archivo = FORMATOS[this.selected.code];
      if (archivo) {
        window.location.href = BUCKET_BASE + archivo;
      }
    },

    uploadFile() {
      this.file = this.$refs.file.files[0];
    },

    /** Sube el soporte al bucket y devuelve la URL guardable. */
    async uploadDocument() {
      try {
        const env = process.env.VUE_APP_ENV === "development" ? "sst-dev" : "sst-prod";
        const path = `${env}/${this.nit}/1.1.6/copasst-doc/type-${this.selected.id}/${this.file.name}`;
        const result = await uploadDocument(path, this.file);
        if (result) {
          return `${process.env.VUE_APP_BUCKET}/${path}`;
        }
        return false;
      } catch (error) {
        console.log(error);
        return false;
      }
    },

    async save() {
      this.submitted = true;
      if (!this.file) {
        Swal.fire({
          title: "Atención",
          text: "Debes seleccionar el soporte a subir.",
          icon: "warning",
          confirmButtonClass: "btn btn-confirm mt-2",
        });
        return;
      }
      try {
        const fileUrl = await this.uploadDocument();
        if (!fileUrl) {
          this.submitted = false;
          Swal.fire({
            title: "Error",
            text: "¡No se pudo subir el soporte!",
            icon: "error",
            confirmButtonClass: "btn btn-confirm mt-2",
          });
          return;
        }
        await CopasstDocAPI.save(this.companyID, this.selected.id, fileUrl);
        await newScoresAPI.calculateScores({
          companies_id: this.companyID,
          item_id: 6,
          status: "Pendiente",
        });
        Swal.fire({
          title: "¡Éxito!",
          text: "¡Se ha registrado el soporte con éxito!",
          icon: "success",
          confirmButtonClass: "btn btn-confirm mt-2",
        });
        setTimeout(() => window.location.reload(), 1500);
      } catch (error) {
        this.submitted = false;
        console.log(error);
        Swal.fire({
          title: "Error",
          text: "¡No se ha podido registrar el soporte!",
          icon: "error",
          confirmButtonClass: "btn btn-confirm mt-2",
        });
      }
    },

    verArchivo(url) {
      window.open(url);
    },

    getFileName(file) {
      try {
        const parts = file.split("/");
        return parts[parts.length - 1];
      } catch (error) {
        return file;
      }
    },
  },

  mounted() {
    this.getTypes();
    this.getHistorial();
  },
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <h5 class="mb-3">Documentos y formatos del COPASST</h5>
      <p class="text-muted">
        Selecciona un documento para descargar el formato y subir el respectivo soporte.
      </p>

      <div v-if="!loading">
        <div class="form-row">
          <b-form-group class="col-md-6 col-sm-12 px-2" label="Documento" label-for="copasstDoc">
            <select id="copasstDoc" v-model="selected" class="form-control">
              <option v-for="dato in list" :value="dato" :key="dato.id">
                {{ dato.code ? dato.code + " - " : "" }}{{ dato.name }}
              </option>
            </select>
          </b-form-group>

          <b-form-group v-if="selected.code" class="col-md-6 col-sm-12 px-2" label="Subir soporte"
            label-for="file">
            <b-form-file :disabled="!getRole('Crear')" type="file" id="file" ref="file" name="file"
              browse-text="Buscar" placeholder="Selecciona un archivo..." drop-placeholder="Suelta acá..."
              v-model="file" @change="uploadFile"
              accept=".doc,.pdf,.xlsx,.xls,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document">
            </b-form-file>
          </b-form-group>
        </div>

        <!-- Acciones para un documento seleccionado -->
        <div v-if="selected.code" class="form-group text-right m-b-0">
          <button type="button" @click="print" class="btn btn-primary mx-1">
            <span class="btn-label"><i class="bx bx-download"></i></span>
            Descargar formato
          </button>
          <button type="button" :disabled="submitted || !getRole('Crear')" @click="save"
            class="btn btn-primary mx-1">
            Guardar soporte
          </button>
        </div>

        <!-- Listado de soportes subidos -->
        <div v-else>
          <table
            class="pt-2 table table-sm table-responsive border-bottom max-w-full divide-y divide-gray-200 table-striped rounded col-sm-12">
            <thead class="table-active">
              <tr>
                <th scope="col" class="px-6 text-left text-xs font-medium text-gray-500 align-middle mx-2">
                  Fecha de subida
                </th>
                <th scope="col" class="px-6 text-left text-xs font-medium text-gray-500 align-middle mx-2">
                  Documento
                </th>
                <th scope="col" class="px-6 text-center text-xs font-medium text-gray-500 align-middle mx-2">
                  Ver archivo
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="dato in historial" :key="dato.id">
                <td class="px-6 text-sm text-gray-500 mx-2 align-middle text-left">
                  {{ dateConvert(dato.created_at) }}
                </td>
                <td class="px-6 text-sm text-gray-500 mx-2 align-middle text-left">
                  {{ dato.code ? dato.code + " - " : "" }}{{ dato.name }}
                </td>
                <td class="px-6 text-sm text-gray-500 mx-2 align-middle text-center">
                  <button style="background-color: transparent !important; border: none;" type="button"
                    @click="verArchivo(dato.file)">
                    <i class="mdi mdi-cloud-download"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="historial.length === 0">
                <td colspan="3" class="px-6 text-sm text-gray-500 mx-2 align-middle text-center">
                  No hay soportes subidos.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="text-center">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>
    </div>
  </div>
</template>
